import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form } from "react-bootstrap";
import Select from 'react-select';
import Swal from "sweetalert2";
import FullPageSpinner from "../LoadingScreen";
import { TextField, FormControlLabel, useMediaQuery, Switch } from "@mui/material";
import { AuthContext } from "../AuthContext";
import { CategoryContext } from "../CategoryContext";
import { LocationsContext } from "../LocationsContext";
import { generateSlug } from "../utils/slug";
import ImageUpload from "../Components/ImageUpload/ImageUpload";
import AIImageUploader from "../Components/AIImageUploader/AIImageUploader";
import AuthModal from "../Components/AuthModal";
import "./PostAd.css";

// ─── AI Processing Overlay ────────────────────────────────────────────────────
function AiProcessingOverlay() {
    return (
        <div className="ai-processing-overlay">
            <div className="ai-scan-lines" />
            <div className="ai-orb-container">
                <div className="ai-orb-ring" />
                <div className="ai-orb-ring" />
                <div className="ai-orb-ring" />
                <div className="ai-orb">
                    <i className="fa-solid fa-wand-magic-sparkles" />
                </div>
            </div>
            <p className="ai-processing-text">Analiziram sliko…</p>
            <div className="ai-particles">
                <div className="ai-particle" />
                <div className="ai-particle" />
                <div className="ai-particle" />
                <div className="ai-particle" />
                <div className="ai-particle" />
            </div>
            <div className="ai-progress-track">
                <div className="ai-progress-bar" />
            </div>
            <p className="ai-processing-subtext">Prepoznavam predmet, ceno in kategorijo</p>
        </div>
    );
}

// ─── Typewriter reveal sequence ───────────────────────────────────────────────
// Steps: 0=idle, 1=typing title, 2=categories visible, 3=typing desc, 4=done
function useAiReveal(fullTitle, fullDesc, active) {
    const [step, setStep]             = useState(0);
    const [titleDisplay, setTitleDisplay] = useState("");
    const [descDisplay, setDescDisplay]   = useState("");
    const timerRef = useRef(null);

    const clearTimer = () => { if (timerRef.current) clearTimeout(timerRef.current); };

    useEffect(() => {
        if (!active) {
            setStep(0);
            setTitleDisplay("");
            setDescDisplay("");
            return;
        }

        // --- Step 1: type title ---
        setStep(1);
        setTitleDisplay("");
        setDescDisplay("");

        let titleIdx = 0;
        const titleSpeed = 28;

        const typeTitle = () => {
            titleIdx++;
            setTitleDisplay(fullTitle.slice(0, titleIdx));
            if (titleIdx < fullTitle.length) {
                timerRef.current = setTimeout(typeTitle, titleSpeed);
            } else {
                // Title done → Step 2: pop in categories
                timerRef.current = setTimeout(() => {
                    setStep(2);
                    // Step 3: start typing description after categories appear
                    timerRef.current = setTimeout(() => {
                        setStep(3);
                        let descIdx = 0;
                        const descSpeed = 9;
                        const typeDesc = () => {
                            descIdx++;
                            setDescDisplay(fullDesc.slice(0, descIdx));
                            if (descIdx < fullDesc.length) {
                                timerRef.current = setTimeout(typeDesc, descSpeed);
                            } else {
                                timerRef.current = setTimeout(() => setStep(4), 200);
                            }
                        };
                        typeDesc();
                    }, 400);
                }, 280);
            }
        };

        timerRef.current = setTimeout(typeTitle, 350);

        return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return {
        step,           // 0=idle 1=typing-title 2=categories 3=typing-desc 4=done
        titleDisplay,
        descDisplay,
    };
}
// ─────────────────────────────────────────────────────────────────────────────

export default function AdForm() {
    const navigate = useNavigate();
    const { user, setUser } = useContext(AuthContext);
    const { cities, loading: locationsLoading } = useContext(LocationsContext);
    const categories = useContext(CategoryContext);

    const [title, setTitle]                           = useState("");
    const [price, setPrice]                           = useState("");
    const [state, setState]                           = useState("");
    const [description, setDescription]               = useState("");
    const [images, setImages]                         = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [finalCategoryId, setFinalCategoryId]       = useState(null);
    const [guestLocationId, setGuestLocationId]       = useState(null);
    const [showAuthModal, setShowAuthModal]           = useState(false);
    const [loading, setLoading]                       = useState(false);
    const isMobile                                    = useMediaQuery('(max-width:767px)');
    const [aiMode, setAiMode]                         = useState(false);
    const [isAiProcessing, setIsAiProcessing]         = useState(false);
    const [aiDataLoaded, setAiDataLoaded]             = useState(false);
    const [aiImageFile, setAiImageFile]               = useState(null);
    const [isUploadingImages, setIsUploadingImages]   = useState(false);

    // Drives the reveal animation
    const { step, titleDisplay, descDisplay } = useAiReveal(title, description, aiDataLoaded);

    // Restore saved data
    useEffect(() => {
        const savedData = localStorage.getItem('tempAdData');
        if (savedData) {
            try {
                const parsed = JSON.parse(savedData);
                if (parsed.title)              setTitle(parsed.title);
                if (parsed.price)              setPrice(parsed.price);
                if (parsed.state)              setState(parsed.state);
                if (parsed.description)        setDescription(parsed.description);
                if (parsed.images)             setImages(parsed.images);
                if (parsed.finalCategoryId)    setFinalCategoryId(parsed.finalCategoryId);
                if (parsed.selectedCategories) setSelectedCategories(parsed.selectedCategories);
                if (parsed.guestLocationId)    setGuestLocationId(parsed.guestLocationId);
            } catch (e) {
                console.error("Error parsing saved ad data", e);
            }
        }
    }, []);

    const findCategoryPath = (categoryId, allCategories) => {
        if (!categoryId || !allCategories) return [];
        let path = [];
        let currentId = categoryId;
        while (currentId !== null) {
            const category = allCategories.find(c => c.id === currentId);
            if (!category) break;
            path.unshift(category.id);
            currentId = category.parent_id;
        }
        return path;
    };

    const handlePriceChange = (e) => {
        let value = e.target.value.replace(/[^\d.,]/g, "");
        if ((value.match(/[.,]/g) || []).length > 1) return;
        if (value === "") { setPrice(""); return; }
        setPrice(value + " €");
    };

    const handleSubmit = async (e, forcedUser = null) => {
        if (e) e.preventDefault();
        const currentUser = forcedUser || user;

        if (!finalCategoryId) { Swal.fire("Napaka!", "Izberite veljavno kategorijo.", "error"); return; }

        const effectiveLocationId = currentUser?.location_id || guestLocationId;
        if (!effectiveLocationId) {
            Swal.fire('Manjka lokacija', 'Prosimo, izberite lokacijo izdelka.', 'warning');
            return;
        }

        if (!currentUser) {
            const tempData = { title, price, state, description, guestLocationId, finalCategoryId, selectedCategories, images };
            localStorage.setItem('tempAdData', JSON.stringify(tempData));
            localStorage.setItem('loginRedirect', '/objavi-oglas');
            localStorage.setItem('autoSubmitAd', 'true');
            setShowAuthModal(true);
            return;
        }

        try {
            const checkResponse = await fetch(`${process.env.REACT_APP_API_URL}/listings/check-duplicate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ title, description }),
            });
            const checkResult = await checkResponse.json();
            if (checkResult.isDuplicate) {
                Swal.fire({
                    title: '<strong>Opa, deja vu! 😵‍💫</strong>',
                    icon: 'info',
                    html: `Točno takšen oglas že imate objavljen!<br/>Ni potrebe po podvajanju.`,
                    showCloseButton: true,
                    showCancelButton: true,
                    focusConfirm: false,
                    confirmButtonText: '<i class="fa fa-arrow-right"></i> Poglej obstoječega',
                    cancelButtonText: 'Uredi tega',
                    background: '#fff url(/images/confetti-bg.png)',
                    backdrop: `rgba(0,0,123,0.4) left top no-repeat`,
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate(`/oglas/${generateSlug(title)}/${checkResult.listingId}`);
                    }
                });
                return;
            }
        } catch (err) {
            console.error("Napaka pri preverjanju duplikata:", err);
        }

        localStorage.removeItem('autoSubmitAd');
        setLoading(true);

        try {
            if (!currentUser.location_id && guestLocationId) {
                await fetch(`${process.env.REACT_APP_API_URL}/users/${currentUser.id}`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ location_id: guestLocationId })
                });
                if (setUser) setUser(prev => ({ ...prev, location_id: guestLocationId }));
            }

            const adData = {
                title,
                description,
                item_condition: state,
                price: parseFloat(price.replace(" €", "").replace(",", ".")) || 0,
                category_id: finalCategoryId,
                user_id: currentUser.id,
                images: images.map(img => ({ image_key: img.image_key, is_primary: img.is_primary })),
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/listings/`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(adData),
            });
            const data = await response.json();

            if (response.status === 403) {
                setLoading(false);
                if (data.user_suspended) {
                    Swal.fire({ title: "Račun onemogočen", text: "Zaradi večkratnih kršitev varnostnih pravil (sum prevare) je vaš račun začasno onemogočen.", icon: "error", confirmButtonText: "Razumem" });
                } else {
                    Swal.fire({
                        title: "Oglas je bil zavrnjen",
                        html: `<div style="text-align:left;margin-top:10px;font-size:15px;"><p>Naš varnostni sistem je zaznal sumljive elemente v vsebini vašega oglasa.</p><p><strong>Utemeljitev:</strong> ${data.reason || "Vsebina ne ustreza pravilom tržnice."}</p><hr/><p>Če menite, da gre za napako, se lahko na odločitev <strong>pritožite</strong> v svoji nadzorni plošči.</p></div>`,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Pojdi v nadzorno ploščo",
                        cancelButtonText: "Zapri",
                        confirmButtonColor: "#463CE5",
                    }).then((result) => { if (result.isConfirmed) navigate("/moj-racun/nadzorna-plosca"); });
                }
                return;
            }

            if (!response.ok) throw new Error(data.message || "Napaka pri shranjevanju oglasa.");

            localStorage.removeItem('tempAdData');
            localStorage.removeItem('loginRedirect');

            Swal.fire({ title: "Uspešno!", html: "Vaš oglas je bil oddan.", icon: "success", confirmButtonText: "Pojdi na oglas" })
                .then(() => {
                    if (data.listing?.id) {
                        navigate(`/oglas/${generateSlug(data.listing.title)}/${data.listing.id}`);
                    } else {
                        navigate("/");
                    }
                });
        } catch (err) {
            Swal.fire("Napaka!", err.message, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const savedDataString = localStorage.getItem('tempAdData');
        const shouldAutoSubmit = localStorage.getItem('autoSubmitAd');
        if (user && shouldAutoSubmit === 'true' && savedDataString) {
            if (locationsLoading) return;
            const savedData = JSON.parse(savedDataString);
            if (savedData.finalCategoryId && !finalCategoryId) return;
            localStorage.removeItem('autoSubmitAd');
            handleSubmit(null, user);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, locationsLoading, finalCategoryId]);

    if (locationsLoading || !categories) return <FullPageSpinner />;

    const cityOptions = cities
        ? cities.map(city => ({ value: city.id, label: city.city + ' (' + city.post_code + ')' }))
        : [];

    const handleAuthSuccess = (loggedInUser) => handleSubmit(null, loggedInUser);

    const conditionOptions = [
        { value: 'Novo',        label: 'Novo' },
        { value: 'Rabljeno',    label: 'Rabljeno' },
        { value: 'Poškodovano', label: 'Poškodovano' }
    ];

    const renderCategorySelectors = () => {
        const levels = [];
        let currentParentId = null;
        for (let level = 0; level <= selectedCategories.length; level++) {
            const categoriesAtLevel = categories.filter(cat => cat.parent_id === currentParentId);
            if (categoriesAtLevel.length === 0) break;
            const currentSelection = selectedCategories[level] || '';
            const options = categoriesAtLevel.map(cat => ({ value: cat.id, label: cat.name }));

            // During AI reveal: hide until step 3 (done), then pop in with stagger
            const aiAnimClass = aiDataLoaded
                ? (step >= 2 ? 'ai-category-pop' : 'ai-category-hidden')
                : '';

            levels.push(
                <div
                    key={level}
                    className={`mb-3 ${aiAnimClass}`}
                    style={{ position: 'relative', zIndex: 1000 - level, '--stagger': level }}
                >
                    <Select
                        className="custom-react-select"
                        classNamePrefix="custom-react-select"
                        value={options.find(option => option.value === currentSelection) || ''}
                        onChange={(selectedOption) => {
                            const newSelected = [...selectedCategories.slice(0, level), selectedOption ? selectedOption.value : ''];
                            setSelectedCategories(newSelected);
                            const hasSub = categories.some(cat => cat.parent_id === (selectedOption ? selectedOption.value : ''));
                            setFinalCategoryId(hasSub ? null : (selectedOption ? selectedOption.value : ''));
                        }}
                        options={options}
                        isClearable
                        placeholder={level === 0 ? "Izberi kategorijo" : "Izberi podkategorijo"}
                        isSearchable={false}
                    />
                </div>
            );
            currentParentId = selectedCategories[level];
        }
        return levels;
    };

    // What to show in each field: animated slice while typing, real value otherwise
    const isCursorOnTitle = aiDataLoaded && step === 1;
    const isCursorOnDesc  = aiDataLoaded && step === 3;
    const titleValue = (aiDataLoaded && step === 1) ? titleDisplay : title;
    const descValue  = (aiDataLoaded && step === 3) ? descDisplay  : description;

    return (
        <>
            <div className="post_item_bg" id="post_item">
                <Container className="post_item_container">
                    <div className="post_item_div">
                        <h2 className="post_item_title mb-4">Objavi oglas</h2>

                        {/* AI toggle banner — shimmer via CSS ::after */}
                        <div className="ai-toggle-container mb-4" style={{ borderRadius: "8px", padding: "2px 16px", backgroundColor: "#fff5f7" }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={aiMode}
                                        onChange={() => {
                                            if (!user) {
                                                Swal.fire({
                                                    icon: 'info',
                                                    title: 'Prijava potrebna',
                                                    text: 'Za uporabo AI pomočnika (analiza slike) se morate prijaviti ali registrirati.',
                                                    confirmButtonText: 'Prijavi se',
                                                    showCancelButton: true,
                                                    cancelButtonText: 'Prekliči'
                                                }).then((result) => {
                                                    if (result.isConfirmed) setShowAuthModal(true);
                                                });
                                                return;
                                            }
                                            setAiMode(!aiMode);
                                            setAiDataLoaded(false);
                                        }}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF4367' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF4367' } }}
                                    />
                                }
                                label={<>Uporabi AI za izpolnitev podatkov <i className="fa-solid fa-wand-magic-sparkles" style={{ color: "#FF4367" }}></i></>}
                            />
                        </div>

                        {aiMode && !aiDataLoaded ? (
                            <div className="ai-processing-wrapper">
                                <AIImageUploader
                                    setIsAiProcessing={setIsAiProcessing}
                                    isAiProcessing={isAiProcessing}
                                    onAnalysisComplete={(data) => {
                                        setTitle(data.title);
                                        setDescription(data.description);
                                        const categoryPath = findCategoryPath(data.categoryId, categories);
                                        setSelectedCategories(categoryPath);
                                        setFinalCategoryId(data.categoryId);
                                        setAiDataLoaded(true);
                                    }}
                                    aiImageFile={aiImageFile}
                                    setAiImageFile={setAiImageFile}
                                />
                                {isAiProcessing && <AiProcessingOverlay />}
                            </div>
                        ) : (
                            <form
                                onSubmit={(e) => handleSubmit(e)}
                                className={aiDataLoaded ? 'ai-form-reveal' : ''}
                            >
                                {/* Info banner shown only during/after AI reveal */}
                                {aiDataLoaded && (
                                    <div className="ai-filled-banner">
                                        <i className="fa-solid fa-wand-magic-sparkles" />
                                        <span>AI je izpolnil podatke — preverite in po potrebi popravite</span>
                                    </div>
                                )}

                                {/* ── Title ── */}
                                <Form.Group className="mb-3">
                                    <div className={`ai-typing-wrapper${isCursorOnTitle ? ' ai-typing-active' : ''}`}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Naziv oglasa"
                                            value={titleValue}
                                            onChange={(e) => setTitle(e.target.value)}
                                            required
                                            readOnly={isCursorOnTitle}
                                        />
                                        {isCursorOnTitle && <span className="ai-cursor" aria-hidden="true" />}
                                    </div>
                                </Form.Group>

                                {/* ── Price ── */}
                                <Row className="cena mb-3">
                                    <Col md={6} className="cena_value">
                                        <div className="price-input-wrapper">
                                            <Form.Control
                                                type="text"
                                                placeholder="Cena"
                                                value={price}
                                                onChange={handlePriceChange}
                                                required
                                                inputMode="decimal"
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                {/* ── Condition ── */}
                                <Row className="stanje mb-3">
                                    <Col md={6} className="stanje_value" style={{ position: 'relative', zIndex: 1006 }}>
                                        <Select
                                            className="custom-react-select"
                                            classNamePrefix="custom-react-select"
                                            value={conditionOptions.find(option => option.value === state)}
                                            onChange={(selectedOption) => setState(selectedOption ? selectedOption.value : '')}
                                            options={conditionOptions}
                                            placeholder="Izberite stanje"
                                            isClearable
                                            isSearchable={false}
                                        />
                                    </Col>
                                </Row>

                                {/* ── Guest location ── */}
                                {(!user || !user.location_id) && (
                                    <div className="mb-3" style={{ position: 'relative', zIndex: 1005 }}>
                                        <h5 className="location_title">Lokacija izdelka</h5>
                                        <Select
                                            className="custom-react-select"
                                            classNamePrefix="custom-react-select"
                                            options={cityOptions}
                                            value={cityOptions.find(c => c.value === guestLocationId)}
                                            onChange={(opt) => setGuestLocationId(opt ? opt.value : null)}
                                            placeholder="Vpišite ali izberite lokacijo..."
                                            isSearchable={true}
                                        />
                                    </div>
                                )}

                                {/* ── Category ── */}
                                <h5 className="category_title">Kategorija izdelka</h5>
                                {renderCategorySelectors()}

                                {/* ── Description ── */}
                                <div className={`ai-typing-wrapper ai-typing-wrapper--textarea${isCursorOnDesc ? ' ai-typing-active' : ''}`}>
                                    <TextField
                                        className="opis_izdelka"
                                        fullWidth
                                        label="Opis izdelka"
                                        multiline
                                        minRows={isMobile ? 10 : 3}
                                        value={descValue}
                                        onChange={(e) => setDescription(e.target.value)}
                                        required
                                        margin="normal"
                                        InputProps={{ readOnly: isCursorOnDesc }}
                                    />
                                    {isCursorOnDesc && <span className="ai-cursor ai-cursor--textarea" aria-hidden="true" />}
                                </div>

                                {/* ── Images ── */}
                                <h5 className="uploitem_picture_title">Slike izdelka</h5>
                                <ImageUpload
                                    images={images}
                                    setImages={setImages}
                                    initialFile={aiDataLoaded ? aiImageFile : null}
                                    setIsUploading={setIsUploadingImages}
                                />

                                <button
                                    type="submit"
                                    className="btn btn-success mt-4 w-100 objavi_oglas_btn"
                                    disabled={loading || isUploadingImages}
                                >
                                    {loading
                                        ? "Objavljanje..."
                                        : isUploadingImages
                                            ? "Nalaganje slik..."
                                            : user ? "Objavi oglas" : "Prijavi se in objavi"}
                                </button>
                            </form>
                        )}
                    </div>
                </Container>
            </div>

            <AuthModal show={showAuthModal} onHide={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />
        </>
    );
}